import styled from 'styled-components';
import { useEffect, useState } from 'react';
import useSWR from 'swr';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { SubscriptionStatus } from 'yo-services/yolabs/services/poplarfrontend/subscription_state_pb';
import { fetcher } from '@yoweb/utils/common';
import { Banner } from '@yoweb/ui/components/Banner';
import { Trans, useTranslation } from '@yoweb/next-i18next';
import { getRadii, getSpace } from '@yoweb/ui/styles/utils/theme';
import { useEntitlement } from '@poplar/contexts/EntitlementProvider';
import { isLocalStorageSupported } from '@yoweb/utils/isSupported';
import WarningIcon from '@yoweb/ui/components/Icon/WarningIcon';
import { InfoIcon } from '@yoweb/ui/components/Icon/InfoIcon';
import { BANNER_HIDDEN_VALUE, LIMITED_REP_BANNER_LOCAL_STORAGE_KEY, PREVIEW_REP_BANNER_LOCAL_STORAGE_KEY, CANCELED_REP_BANNER_LOCAL_STORAGE_KEY, CANCELED_NEW_REP_BANNER_LOCAL_STORAGE_KEY, PAUSED_REP_BANNER_LOCAL_STORAGE_KEY, REP_BANNER_ID, UPCOMING_PAUSE_REP_BANNER_LOCAL_STORAGE_KEY } from '@poplar/constants/banners';
import { AccessType } from '@poplar/types/enums';
import { URL_CHECKOUT_PLAN } from '@poplar/constants/urls';
import { PAGE_SUBSCRIPTION, API_SUBSCRIPTION } from '@poplar/constants/routes';
import { useExternalConfig } from '@poplar/contexts/ExternalConfig';
import { Text } from '@yoweb/ui/components/typography';
import { Link } from '@yoweb/ui/components/Link';
import { formatDate } from '@yoweb/utils/date';
import { useUser } from '@poplar/hooks/useUser';
import { jsx as _jsx } from "react/jsx-runtime";

var canBannerBeShown = function canBannerBeShown(key) {
  return localStorage.getItem(key) !== BANNER_HIDDEN_VALUE;
};

export var useRepBanner = function useRepBanner() {
  var _useEntitlement = useEntitlement(),
      accessType = _useEntitlement.accessType;

  var _useState = useState(false),
      isReady = _useState[0],
      setIsReady = _useState[1];

  var _useState2 = useState(null),
      banner = _useState2[0],
      setBanner = _useState2[1];

  var _useSWR = useSWR(API_SUBSCRIPTION, fetcher, {
    revalidateOnFocus: false
  }),
      subscription = _useSWR.data;

  var hasFullAccess = accessType === AccessType.FULL && (subscription === null || subscription === void 0 ? void 0 : subscription.status) !== SubscriptionStatus.SUBSCRIPTION_STATUS_PENDING_PAUSE;
  useEffect(function () {
    setIsReady(true);

    if (!isLocalStorageSupported() || hasFullAccess) {
      return;
    }

    if (accessType === AccessType.LIMITED && canBannerBeShown(LIMITED_REP_BANNER_LOCAL_STORAGE_KEY)) {
      setBanner(LIMITED_REP_BANNER_LOCAL_STORAGE_KEY);
    } else if (accessType === AccessType.PREVIEW && canBannerBeShown(PREVIEW_REP_BANNER_LOCAL_STORAGE_KEY)) {
      setBanner(PREVIEW_REP_BANNER_LOCAL_STORAGE_KEY);
    } else if (accessType === AccessType.PAUSED && canBannerBeShown(PAUSED_REP_BANNER_LOCAL_STORAGE_KEY)) {
      setBanner(PAUSED_REP_BANNER_LOCAL_STORAGE_KEY);
    } else if (accessType === AccessType.CANCELED && canBannerBeShown(CANCELED_NEW_REP_BANNER_LOCAL_STORAGE_KEY)) {
      setBanner(CANCELED_NEW_REP_BANNER_LOCAL_STORAGE_KEY);
    } else if ((subscription === null || subscription === void 0 ? void 0 : subscription.status) === SubscriptionStatus.SUBSCRIPTION_STATUS_PENDING_PAUSE && canBannerBeShown(UPCOMING_PAUSE_REP_BANNER_LOCAL_STORAGE_KEY)) {
      setBanner(UPCOMING_PAUSE_REP_BANNER_LOCAL_STORAGE_KEY);
    }
  }, [accessType, hasFullAccess, subscription]);

  if (hasFullAccess) {
    // Note: when full access we do not have to wait for the client logic to initialize
    // since there is no subscription banner state for fully active users
    return {
      banner: null,
      isReady: true
    };
  }

  return {
    banner: banner,
    subscription: subscription,
    isReady: isReady
  };
};
export var RepBanner = function RepBanner(_ref) {
  var banner = _ref.banner,
      subscription = _ref.subscription;

  var _useTranslation = useTranslation('common'),
      t = _useTranslation.t,
      i18n = _useTranslation.i18n;

  var user = useUser();

  var _useExternalConfig = useExternalConfig(),
      baseStoreUrl = _useExternalConfig.baseStoreUrl,
      enableGuideDeprecation = _useExternalConfig.globalFeatures.enableGuideDeprecation;

  if (!banner) {
    return null;
  }

  var handleDismissBanner = function handleDismissBanner(key) {
    if (!isLocalStorageSupported()) {
      return;
    }

    localStorage.setItem(key, BANNER_HIDDEN_VALUE);
  };

  var bannerInfo;

  switch (banner) {
    case LIMITED_REP_BANNER_LOCAL_STORAGE_KEY:
      if (enableGuideDeprecation) {
        return null;
      }

      bannerInfo = {
        title: t('limited-access-banner-title'),
        subtitle: t('limited-access-banner-subtitle'),
        onDismiss: function onDismiss() {
          return handleDismissBanner(LIMITED_REP_BANNER_LOCAL_STORAGE_KEY);
        },
        background: 'infoLight',
        icon: /*#__PURE__*/_jsx(InfoIcon, {
          size: 24,
          variant: "info"
        })
      };
      break;

    case PAUSED_REP_BANNER_LOCAL_STORAGE_KEY:
      {
        var _subscription$pauseIn, _subscription$pauseIn2;

        var resumeDatetime = subscription === null || subscription === void 0 ? void 0 : (_subscription$pauseIn = subscription.pauseInfo) === null || _subscription$pauseIn === void 0 ? void 0 : (_subscription$pauseIn2 = _subscription$pauseIn.resumeDateTime) === null || _subscription$pauseIn2 === void 0 ? void 0 : _subscription$pauseIn2.seconds;
        bannerInfo = {
          subtitle: /*#__PURE__*/_jsx(Trans, {
            t: t,
            i18nKey: "paused-access-banner-subtitle",
            values: {
              pauseReturn: resumeDatetime ? formatDate(new Date(resumeDatetime * 1000), {
                dateStyle: 'long',
                locale: i18n.language,
                timeZone: user.timeZone
              }) : undefined
            },
            components: [/*#__PURE__*/_jsx(BoldLink, {
              href: PAGE_SUBSCRIPTION,
              hasUnderline: true,
              "data-testid": "paused-access-banner-cta"
            }, "paused-reactivate-link")]
          }),
          onDismiss: function onDismiss() {
            return handleDismissBanner(PAUSED_REP_BANNER_LOCAL_STORAGE_KEY);
          },
          background: 'warningLight',
          icon: /*#__PURE__*/_jsx(WarningIcon, {
            size: 24,
            variant: "warning"
          })
        };
        break;
      }

    case CANCELED_NEW_REP_BANNER_LOCAL_STORAGE_KEY:
      bannerInfo = {
        title: t('canceled-new-access-banner-title'),
        subtitle: /*#__PURE__*/_jsx(Trans, {
          t: t,
          i18nKey: "canceled-new-access-banner-subtitle",
          components: [/*#__PURE__*/_jsx(Text, {
            as: "a",
            href: URL_CHECKOUT_PLAN(baseStoreUrl, i18n.language),
            rel: "noopener noreferrer",
            size: "md",
            textDecoration: "underline",
            target: "_blank",
            "data-testid": "canceled-access-banner-cta",
            variant: "fg"
          }, 0)]
        }),
        onDismiss: function onDismiss() {
          return handleDismissBanner(CANCELED_NEW_REP_BANNER_LOCAL_STORAGE_KEY);
        },
        background: 'warningLight',
        icon: /*#__PURE__*/_jsx(WarningIcon, {
          size: 24,
          variant: "warning"
        })
      };
      break;

    case CANCELED_REP_BANNER_LOCAL_STORAGE_KEY:
      bannerInfo = {
        title: t('canceled-access-banner-title'),
        subtitle: /*#__PURE__*/_jsx(Trans, {
          t: t,
          i18nKey: "canceled-access-banner-subtitle",
          components: [/*#__PURE__*/_jsx(Text, {
            as: "a",
            href: URL_CHECKOUT_PLAN(baseStoreUrl, i18n.language),
            rel: "noopener noreferrer",
            size: "md",
            textDecoration: "underline",
            target: "_blank",
            "data-testid": "canceled-access-banner-cta",
            variant: "fg"
          }, 0)]
        }),
        onDismiss: function onDismiss() {
          return handleDismissBanner(CANCELED_REP_BANNER_LOCAL_STORAGE_KEY);
        },
        background: 'warningLight',
        icon: /*#__PURE__*/_jsx(WarningIcon, {
          size: 24,
          variant: "warning"
        })
      };
      break;

    case PREVIEW_REP_BANNER_LOCAL_STORAGE_KEY:
      bannerInfo = {
        title: t('preview-access-banner-title'),
        subtitle: /*#__PURE__*/_jsx(Trans, {
          t: t,
          i18nKey: "preview-access-banner-subtitle",
          components: [/*#__PURE__*/_jsx(Text, {
            as: "a",
            href: URL_CHECKOUT_PLAN(baseStoreUrl, i18n.language),
            rel: "noopener noreferrer",
            size: "md",
            textDecoration: "underline",
            target: "_blank",
            "data-testid": "preview-access-banner-cta",
            variant: "fg"
          }, 0)]
        }),
        onDismiss: function onDismiss() {
          return handleDismissBanner(PREVIEW_REP_BANNER_LOCAL_STORAGE_KEY);
        },
        background: 'infoLight',
        icon: /*#__PURE__*/_jsx(InfoIcon, {
          size: 24,
          variant: "info"
        })
      };
      break;

    case UPCOMING_PAUSE_REP_BANNER_LOCAL_STORAGE_KEY:
      {
        var _subscription$pauseIn3, _subscription$pauseIn4;

        var pauseDatetime = subscription === null || subscription === void 0 ? void 0 : (_subscription$pauseIn3 = subscription.pauseInfo) === null || _subscription$pauseIn3 === void 0 ? void 0 : (_subscription$pauseIn4 = _subscription$pauseIn3.pauseStartDateTime) === null || _subscription$pauseIn4 === void 0 ? void 0 : _subscription$pauseIn4.seconds;
        if (!pauseDatetime) return null;
        bannerInfo = {
          subtitle: /*#__PURE__*/_jsx(Trans, {
            t: t,
            i18nKey: "upcoming-pause-access-banner-subtitle",
            values: {
              pauseDate: formatDate(new Date(pauseDatetime * 1000), {
                dateStyle: 'long',
                locale: i18n.language,
                timeZone: user.timeZone
              })
            }
          }),
          onDismiss: function onDismiss() {
            return handleDismissBanner(UPCOMING_PAUSE_REP_BANNER_LOCAL_STORAGE_KEY);
          },
          background: 'warningLight',
          icon: /*#__PURE__*/_jsx(InfoIcon, {
            size: 24,
            variant: "info"
          })
        };
        break;
      }

    default:
      return null;
  }

  return /*#__PURE__*/_jsx(StyledBanner, {
    backgroundColor: bannerInfo.background,
    onDismiss: bannerInfo.onDismiss,
    icon: bannerInfo.icon,
    dismissible: true,
    title: bannerInfo.title,
    subtitle: bannerInfo.subtitle // Needed to calculate max-height of chat on the /chat page
    ,
    id: REP_BANNER_ID,
    "data-testid": "rep-banner"
  });
};
var StyledBanner = styled(Banner).withConfig({
  componentId: "sc-1ie9k10-0"
})(["&&{border-radius:", ";padding:1.25rem ", ";margin-bottom:", ";border:none;}"], getRadii('small'), getSpace('normal1'), getSpace('medium1'));
var BoldLink = styled(Link).withConfig({
  componentId: "sc-1ie9k10-1"
})(["&&{font-weight:600;}"]);